import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import useModal from "hooks/modals/useModal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash, faPencil, faCopy, faTimes } from "@fortawesome/pro-regular-svg-icons";
import moment from "moment";

import { 
    GetBookingRestrictions, 
    PostBookingRestriction,
    PutBookingRestriction,
    DeleteBookingRestriction,
    PutDuplicateBookingRestriction,
} from "api/rpc/2024-04/facilityAdmin/teesheet/booking";
import { GetCustomerType } from "api/rpc/2024-04/facilityAdmin/customer/type";
import { StatusCode } from "api/protocols";
import axios, { CancelToken } from "axios";

import { showError, showSuccess } from "redux/actions/ui";
import { calculateHexColorBrightness } from "helpers/Helpers";

import Page from "components/page/Page";
import DataTable from "components/dataTable/DataTable";
import Portal from "elements/Portal";
import Sheet from "components/sheet/Sheet";
import TimePick from "components/timePick/TimePick";
import FormLayout from "components/form/FormLayout";
import SelectNew from "components/select/SelectNew";
import { Select } from "components/select/index";
import { TCustomerType } from "redux/reducers/models/facility";
import { Badge } from "components/badge/Badge";
import Popup from "components/popup/Popup";
import Toggle from "components/form/toggle/Toggle";
import Icon from "components/icon/Icon";
import Card from "components/card/Card";
import { Day, DayValue } from "pages/secure/facility/settings/teesheets/pricingSheets/TeeSheetPricingSheets";
import MessageDisplay from "components/messageDisplay/MessageDisplay";

import "./bookingRestrictions.scss";

export type TBookingRestriction = {
    id: number | string;
    facility_id: number | string;
    start_time: string;
    end_time: string;
    day_of_week: string;
    day_of_week_number: number;
    block_bookings: boolean;
    customer_type: TCustomerType;
    customer_type_id: number;
}

const weekdayNames = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday", "Holiday"];

export default function BookingRestrictions() {
    const { t } = useTranslation(); 
    const dispatch = useAppDispatch();
    const authStore = useAppSelector(store => store.authStore);
    const permissions = authStore.user.permissions;
    
    const [restrictions, setRestrictions] = useState<TBookingRestriction[]>(undefined);
    const [customerTypes, setCustomerTypes] = useState<TCustomerType[]>(undefined);
    const [filterDayState, setFilterDayState] = useState<DayValue>(Day.Monday);

    const {
        state: newRestrictionModal,
        updateModal: updateNewRestrictionModal,
        closeModal: closeNewRestrictionModal,
      } = useModal({
        start_time: "",
        end_time: "",
        day_of_week: "Monday",
        prohibit_bookings: false,
        customer_types: [] as TCustomerType[],
        clearOnClose: true,
        customerTypeSearch: "",
      });

      const {
        state: editRestrictionModal,
        updateModal: updateEditRestrictionModal,
        closeModal: closeEditRestrictionModal,
      } = useModal({
        id: null,
        start_time: "",
        end_time: "",
        day_of_week: "Monday",
        prohibit_bookings: false,
        customer_type: null as TCustomerType,
        clearOnClose: true,
        customerTypeSearch: "",
      });

      const {
        state: duplicateRestrictionModal,
        updateModal: updateDuplicateRestrictionModal,
        closeModal: closeDuplicateRestrictionModal,
      } = useModal({
        id: null,
        days_of_week: [] as string[],
        customer_types: [] as TCustomerType[],
        clearOnClose: true,
        customerTypeSearch: "",
      });

      const {
        state: deleteRestrictionModal,
        updateModal: updateDeleteRestrictionModal,
        closeModal: closeDeleteRestrictionModal,
      } = useModal({
        id: null,
      });

    useEffect(() => {
        const source = axios.CancelToken.source();
        void loadBookingRestrictions(source.token);
        return () => source.cancel();
    }, []);

    useEffect(() => {
        const source = axios.CancelToken.source();
        void loadCustomerTypes(source.token);
        return () => source.cancel();
    }, [newRestrictionModal.customerTypeSearch]);

    async function loadBookingRestrictions(token?: CancelToken) {
        setRestrictions(undefined);

        const restrictionsRes = await GetBookingRestrictions({ extended: true }, false, token);

        if (token && token.reason) {
            return;
        }
        if (restrictionsRes.status !== StatusCode.OK) {
            dispatch(showError(restrictionsRes.message ?? "Error loading booking restrictions"));
            setRestrictions([]);
            return;
        }

        setRestrictions(restrictionsRes.data);
    }

    async function loadCustomerTypes(token?: CancelToken) {
        const typeRes = await GetCustomerType({ 
            search: newRestrictionModal.customerTypeSearch,
            application: "green_fee", 
        }, false, token);

        if (token && token.reason) {
            return;
        }
        if (typeRes.status !== StatusCode.OK) {
            dispatch(showError(typeRes.message ?? "Error loading customer types"));
            setRestrictions([]);
            return;
        }

        setCustomerTypes(typeRes.data);
    }

    async function createBookingRestriction() {
        const restrictionRes = await PostBookingRestriction({
            start_time: newRestrictionModal.start_time,
            end_time: newRestrictionModal.end_time,
            day_of_week: newRestrictionModal.day_of_week,
            block_bookings: newRestrictionModal.prohibit_bookings,
            customer_type_ids: newRestrictionModal.customer_types.map(type => ( type.id )),
        }, true );

        if (restrictionRes.status !== StatusCode.OK) {
            dispatch(showError(restrictionRes.message ?? "Error creating booking restriction"));
            return;
        }

        dispatch(showSuccess("Booking restriction created successfully"));
        void loadBookingRestrictions();
        closeNewRestrictionModal();
    }

    async function updateBookingRestriction() {
        const restrictionRes = await PutBookingRestriction({
            id: editRestrictionModal.id,
            start_time: editRestrictionModal.start_time,
            end_time: editRestrictionModal.end_time,
            day_of_week: editRestrictionModal.day_of_week,
            block_bookings: editRestrictionModal.prohibit_bookings,
            customer_type_id: editRestrictionModal.customer_type.id,
        }, true );

        if (restrictionRes.status !== StatusCode.OK) {
            dispatch(showError(restrictionRes.message ?? "Error creating booking restriction"));
            return;
        }

        dispatch(showSuccess("Booking restriction updated successfully"));
        void loadBookingRestrictions();
        closeEditRestrictionModal();
    }

    async function deleteBookingRestriction() {
        if (!deleteRestrictionModal.id) {
            dispatch(showError(t("No restriction selected")));
        }
        
        const deleteRes = await DeleteBookingRestriction({ id: deleteRestrictionModal.id }, true);

        if (deleteRes.status !== StatusCode.OK) {
            dispatch(showError(deleteRes.data as string ?? "Error creating booking restriction"));
            return;
        }

        dispatch(showSuccess("Booking restriction deleted successfully"));
        void loadBookingRestrictions();
        closeDeleteRestrictionModal();
    }

    async function duplicateBookingRestriction() {
        const duplicateRes = await PutDuplicateBookingRestriction({
            id: duplicateRestrictionModal.id,
            customer_type_ids: duplicateRestrictionModal.customer_types.map(type => ( type.id )),
            days_of_week: duplicateRestrictionModal.days_of_week,
        }, true );

        if (duplicateRes.status !== StatusCode.OK) {
            dispatch(showError(duplicateRes.data as string ?? "Error duplicating booking restriction"));
            return;
        }

        dispatch(showSuccess("Booking restriction duplicated successfully"));
        void loadBookingRestrictions();
        closeDuplicateRestrictionModal();
    }

    function handleEditRestriction(restriction: TBookingRestriction) {
        updateEditRestrictionModal({
            isOpen: true,
            id: restriction.id,
            start_time: restriction.start_time,
            end_time: restriction.end_time,
            day_of_week: restriction.day_of_week,
            customer_type: restriction.customer_type,
            prohibit_bookings: restriction.block_bookings,
        });
    }

    function handleSelectCustomerType(customerType: TCustomerType, type: "create" | "edit" | "duplicate") {
        if (type === "create") {
            const selectedCustomerTypes = [...newRestrictionModal.customer_types];
            const typeExists = selectedCustomerTypes?.find(type => type.id === customerType.id);
            if (typeExists) {
                updateNewRestrictionModal({
                    customer_types: selectedCustomerTypes?.filter(type => type.id !== customerType.id),
                });
            } else {
                selectedCustomerTypes.push(customerType);
                updateNewRestrictionModal({ customer_types: selectedCustomerTypes});
            }
        } else if (type === "duplicate") {
            const selectedCustomerTypes = [...duplicateRestrictionModal.customer_types];
            const typeExists = selectedCustomerTypes?.find(type => type.id === customerType.id);
            if (typeExists) {
                updateDuplicateRestrictionModal({
                    customer_types: selectedCustomerTypes?.filter(type => type.id !== customerType.id),
                });
            } else {
                selectedCustomerTypes.push(customerType);
                updateDuplicateRestrictionModal({ customer_types: selectedCustomerTypes});
            }
        } else {
            updateEditRestrictionModal({ customer_type: customerType });
        }
    }

    function handleSelectDayOfWeek(dayOfWeek: string) {
        const selectedDaysOfWeek = [...duplicateRestrictionModal.days_of_week];
        const dayExists = selectedDaysOfWeek?.find(day => day === dayOfWeek);
        if (dayExists) {
            updateDuplicateRestrictionModal({
                days_of_week: selectedDaysOfWeek?.filter(day => day !== dayOfWeek),
            });
        } else {
            selectedDaysOfWeek.push(dayOfWeek);
            updateDuplicateRestrictionModal({ days_of_week: selectedDaysOfWeek});
        }
    }

    function DayTab({ title, value }: { title: string; value: DayValue }): JSX.Element {
        return (
            <button
                className="flex-1 py-2 rounded-t-m"
                style={
                filterDayState === value
                    ? { backgroundColor: "#e7e7e7", borderTopLeftRadius: "7px", borderTopRightRadius: "7px" }
                    : { backgroundColor: "#ffffff" }
                }
                onClick={() => setFilterDayState(value)}
            >
                {title}
            </button>
        );
    }

    return (
        <Page
            title={t("Booking Restrictions")}
            narrow
            primaryAction={{
                action: () => updateNewRestrictionModal({ isOpen: true, customerTypeSearch: "" }),
                content: t("New Restriction"),
                disabled: !permissions.booking_restrictions_edit,
            }}
        >
            <Card>
                <Card.Section table="true">
                    <div className="flex">
                        <DayTab title={t("Monday")} value={Day.Monday} />
                        <DayTab title={t("Tuesday")} value={Day.Tuesday} />
                        <DayTab title={t("Wednesday")} value={Day.Wednesday} />
                        <DayTab title={t("Thursday")} value={Day.Thursday} />
                        <DayTab title={t("Friday")} value={Day.Friday} />
                        <DayTab title={t("Saturday")} value={Day.Saturday} />
                        <DayTab title={t("Sunday")} value={Day.Sunday} />
                        <DayTab title={t("Holiday")} value={Day.Holiday} />
                    </div>
                    {restrictions === undefined || restrictions
                        ?.filter(restriction => restriction.day_of_week_number === filterDayState || (filterDayState === Day.Holiday && restriction.day_of_week_number === 99)).length > 0 ? (
                        <DataTable
                            columns={[
                                { label: t("Day Of Week") },
                                { label: t("Start Time")},
                                { label: t("End Time") },
                                { label: t("Customer Type"), width: "50%" },
                                { label: "" },
                            ]}
                            loading={restrictions === undefined}
                        >
                            {restrictions
                                ?.filter(restriction => restriction.day_of_week_number === filterDayState || (filterDayState === Day.Holiday && restriction.day_of_week_number === 99))
                                ?.map((restriction, index) => (
                                <tr key={index}>
                                    <td>{restriction.day_of_week}</td>
                                    <td>{moment(restriction.start_time, ["HH:mm"]).format("h:mm A")}</td>
                                    <td>{moment(restriction.end_time, ["HH:mm"]).format("h:mm A")}</td>
                                    <td>
                                        <Badge 
                                            backgroundColour={restriction.customer_type.color}
                                            colour={restriction.customer_type.color ? calculateHexColorBrightness(restriction.customer_type.color) < 50 ? "white" : "black" : undefined}
                                        >
                                            {`${restriction.customer_type.title}${restriction.customer_type.subtitle ? " - " : ""}${restriction.customer_type.subtitle ? restriction.customer_type.subtitle : ""}`}
                                        </Badge>
                                    </td>
                                    {permissions.booking_restrictions_edit ? (
                                        <td className="clickable">
                                            <FontAwesomeIcon className="mr-2" icon={faPencil} onClick={() => handleEditRestriction(restriction)} />
                                            <FontAwesomeIcon className="mr-2" icon={faCopy} onClick={() => updateDuplicateRestrictionModal({ isOpen: true, id: restriction.id })} />
                                            <FontAwesomeIcon icon={faTrash} onClick={() => updateDeleteRestrictionModal({ isOpen: true, id: restriction.id })} /> 
                                        </td>
                                    ) : <td />}
                                    
                                </tr>
                            ))}
                        </DataTable>
                    ) : (
                        <div className="p-4">
                            <MessageDisplay
                                title={t("No Restrictions")}
                                icon={"circle-exclamation"}
                                content={t("No booking restrictions exist for the selected day of the week.")}
                                actions={[
                                    {
                                        label: t("+ Create Restriction"),
                                        action: () => updateNewRestrictionModal({ isOpen: true , customerTypeSearch: ""}),
                                        disabled: !permissions.booking_restrictions_edit,
                                    },
                                ]}
                            />
                        </div>
                    )}
                </Card.Section>
            </Card>

            <Portal isMounted={newRestrictionModal.isOpen}>
                <Sheet
                    size="small"
                    open={newRestrictionModal.isOpen}
                    title={t("New Booking Restriction")}
                    onCancel={closeNewRestrictionModal}
                    onOk={() => void createBookingRestriction()}
                    okText={t("Create")}
                    overflow
                    closable
                >
                    <FormLayout>
                        <FormLayout.Group>
                            <div>
                                <SelectNew
                                    id={"new_restriction_modal_day_select"}
                                    label={t("Day of Week")}
                                    value={newRestrictionModal.day_of_week}
                                    options={weekdayNames.map(name => ({ id: name, label: name }))}
                                    onChange={value => updateNewRestrictionModal({ day_of_week: value })}
                                />
                            </div>
                        </FormLayout.Group>
            
                        <FormLayout.Group>
                            <div>
                                <TimePick
                                    value={newRestrictionModal.start_time}
                                    onChange={timeString => updateNewRestrictionModal({ start_time: timeString })}
                                    label={t("Start Time")}
                                    size="large"
                                    status={newRestrictionModal.start_time === undefined ? "warning" : undefined}
                                    positionTop
                                />
                            </div>
            
                            <div>
                                <TimePick
                                    value={newRestrictionModal.end_time}
                                    onChange={timeString => updateNewRestrictionModal({ end_time: timeString })}
                                    label={t("End Time")}
                                    size="large"
                                    status={newRestrictionModal.end_time === undefined ? "warning" : undefined}
                                    positionTop
                                />
                            </div>
                        </FormLayout.Group>
                        <FormLayout.Group>
                            <Toggle
                                label={t("Block Bookings")}
                                labelSupport={t("This will completely prevent customers of the selected customer types from making bookings during these hours")}
                                checked={newRestrictionModal.prohibit_bookings}
                                onChange={e => updateNewRestrictionModal({ prohibit_bookings: e.target.checked })}
                            />
                        </FormLayout.Group>
                        <FormLayout.Group>
                            <SelectNew
                                label={t("Select customer types")}
                                id={"new_restriction_customer_type_select"}
                                options={customerTypes?.map(type => ({ id: type.id, label: `${type.title}${type.subtitle ? " - " : ""}${type.subtitle ? type.subtitle : ""}`, extraValue: type }))}
                                onChange={(value: number, extraValue: TCustomerType) => handleSelectCustomerType(extraValue, "create") }
                                multiValue={newRestrictionModal?.customer_types?.map(type => type?.id)}
                                onSearchCallback={(query: string) => updateNewRestrictionModal({ customerTypeSearch: query })}
                                searchable
                                showAmountSelected
                            />
                        </FormLayout.Group>
                    </FormLayout>
                </Sheet>
            </Portal>

            <Portal isMounted={editRestrictionModal.isOpen}>
                <Sheet
                    size="small"
                    open={editRestrictionModal.isOpen}
                    title={t("Edit Booking Restriction")}
                    onCancel={closeEditRestrictionModal}
                    onOk={() => void updateBookingRestriction()}
                    okText={t("Update")}
                    overflow
                    closable
                >
                    <FormLayout>
                        <FormLayout.Group>
                            <div>
                                <SelectNew
                                    id={"new_restriction_modal_day_select"}
                                    label={t("Day of Week")}
                                    value={editRestrictionModal.day_of_week}
                                    options={weekdayNames.map(name => ({ id: name, label: name }))}
                                    onChange={value => updateEditRestrictionModal({ day_of_week: value })}
                                />
                            </div>
                        </FormLayout.Group>
            
                        <FormLayout.Group>
                            <div>
                                <TimePick
                                    value={editRestrictionModal.start_time}
                                    onChange={timeString => updateEditRestrictionModal({ start_time: timeString })}
                                    label={t("Start Time")}
                                    size="large"
                                    status={editRestrictionModal.start_time === undefined ? "warning" : undefined}
                                    positionTop
                                />
                            </div>
            
                            <div>
                                <TimePick
                                    value={editRestrictionModal.end_time}
                                    onChange={timeString => updateEditRestrictionModal({ end_time: timeString })}
                                    label={t("End Time")}
                                    size="large"
                                    status={editRestrictionModal.end_time === undefined ? "warning" : undefined}
                                    positionTop
                                />
                            </div>
                        </FormLayout.Group>
                        <FormLayout.Group>
                            <Toggle
                                label={t("Block Bookings")}
                                labelSupport={t("This will completely prevent customers of the selected customer types from making bookings during these hours")}
                                checked={editRestrictionModal.prohibit_bookings}
                                onChange={e => updateEditRestrictionModal({ prohibit_bookings: e.target.checked })}
                            />
                        </FormLayout.Group>
                        <FormLayout.Group>
                            {editRestrictionModal.customer_type ? (
                                <div className="booking-restriction-customer-type-dropdown">
                                    <div>
                                        <p className="event-label mb-1">
                                            {" "}
                                            {t("Customer Type")}{" "}
                                            <div></div>
                                        </p>
                                    </div>
            
                                    <div className="selected-container">
                                    <div className="event-name">
                                        <div>{`${editRestrictionModal.customer_type.title}${editRestrictionModal.customer_type.subtitle ? " - " : ""}${editRestrictionModal.customer_type.subtitle ? editRestrictionModal.customer_type.subtitle : ""}`}</div>
            
                                        <div>
                                        <button className="" onClick={() => updateEditRestrictionModal({ customer_type: null })}>
                                            <Icon icon={faTimes} />
                                        </button>
                                        </div>
                                    </div>
                                    </div>
                                </div>
                                ) : (
                                <Select
                                    label={t("Customer Type")}
                                    showSearch
                                    showDropDownOnFocus={true}
                                    placeholder={t("Search customer types...")}
                                    onSearch={(query: string) => updateEditRestrictionModal({ customerTypeSearch: query })}
                                    onChange={(value: number, extraValues: TCustomerType) => handleSelectCustomerType(extraValues, "edit") }
                                >
                                    {customerTypes?.map(type => (
                                        <Select.Option 
                                            key={type.id}
                                            value={type.id}
                                            extraValues={type}
                                        >
                                            {`${type.title}${type.subtitle ? " - " : ""}${type.subtitle ? type.subtitle : ""}`}
                                        </Select.Option>
                                    ))}
                                </Select>
                                )}
                        </FormLayout.Group>
                    </FormLayout>
                </Sheet>
            </Portal>

            <Portal isMounted={duplicateRestrictionModal.isOpen}>
                <Sheet
                    size="small"
                    open={duplicateRestrictionModal.isOpen}
                    title={t("Duplicate Booking Restriction")}
                    onCancel={closeDuplicateRestrictionModal}
                    onOk={() => void duplicateBookingRestriction()}
                    okText={t("Duplicate")}
                    overflow
                    closable
                >
                    <FormLayout>
                        <FormLayout.Group>
                            <SelectNew
                                label={t("Select days to copy this restriction to")}
                                id={"new_restriction_modal_day_select"}
                                options={weekdayNames.map(name => ({ id: name, label: name }))}
                                onChange={value => handleSelectDayOfWeek(value)}
                                multiValue={duplicateRestrictionModal?.days_of_week}
                            />
                        </FormLayout.Group>
                        <FormLayout.Group>
                            <SelectNew
                                label={t("Select customer types to copy this restriction for")}
                                id={"new_restriction_modal_day_select"}
                                options={customerTypes?.map(type => ({ id: type.id, label: `${type.title}${type.subtitle ? " - " : ""}${type.subtitle ? type.subtitle : ""}`, extraValue: type }))}
                                onChange={(value: number, extraValue: TCustomerType) => handleSelectCustomerType(extraValue, "duplicate") }
                                multiValue={duplicateRestrictionModal?.customer_types?.map(type => type?.id)}
                                onSearchCallback={(query: string) => updateNewRestrictionModal({ customerTypeSearch: query })}
                                searchable
                            />
                        </FormLayout.Group>
                    </FormLayout>
                </Sheet>
            </Portal>

            <Portal isMounted={deleteRestrictionModal.isOpen}>
                <Popup
                    open={deleteRestrictionModal.isOpen}
                    type="warning"
                    onCancel={closeDeleteRestrictionModal}
                    onOk={() => void deleteBookingRestriction()}
                    title={t("Delete Booking Restriction")}
                    description={t("Are you sure you want to delete this booking restriction?")}
                    okText={t("Delete")}
                />
            </Portal>
        </Page>
    );
}